import React, { useState } from 'react';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import SubscriptionUpgradeModal from '../components/SubscriptionUpgradeModal';
import AddonManagement from '../components/AddonManagement';
import AddonPurchaseModal from '../components/AddonPurchaseModal';

const SubscriptionManagement: React.FC = () => {
  const { subscription, addons, isLoading, error, refreshSubscription } = useSubscriptionContext();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'essential' | 'gold' | 'premium'>('essential');

  const plans = [
    {
      type: 'essential' as const,
      name: 'Essential Plan',
      price: 799,
      features: [
        'BCP aligned with ISO 22301:2019 (Flash AI)',
        'Incident response checklists & SOPs',
        'Basic customer support'
      ],
      popular: false
    },
    {
      type: 'gold' as const,
      name: 'Gold Plan',
      price: 1999,
      features: [
        'All Essential Features',
        'Flash RTC out-of-band communication',
        'Dedicated onboarding support'
      ],
      popular: true
    },
    {
      type: 'premium' as const,
      name: 'Premium Plan',
      price: 3999,
      features: [
        'All Gold Features',
        'Executive leadership training modules',
        'ISO 27001 readiness (cybersecurity)',
        'Customized tabletop scenarios & insurer reports',
        'Resilience Badge (up to 10% insurance discount)',
        'Priority support & quarterly check-ins'
      ],
      popular: false
    }
  ];

  const handleUpgrade = (planType: 'essential' | 'gold' | 'premium') => {
    setSelectedPlan(planType);
    // Go directly to Stripe payment form, bypassing plan selection modal
    setShowUpgradeModal(true);
  };


  const getCurrentPlan = () => {
    if (!subscription) return null;
    return plans.find(plan => plan.type === subscription.plan_type);
  };

  const getPlanStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'past_due':
        return 'text-red-600 bg-red-100';
      case 'canceled':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={refreshSubscription}
            className="mt-2 text-red-800 underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Current Subscription Status */}
      {subscription && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Current Plan</h2>
              <p className="text-gray-600 mt-1">
                {getCurrentPlan()?.name} - ${getCurrentPlan()?.price}/year
                {addons && addons.filter(a => a.status === 'active').length > 0 && (
                  <span className="ml-2 text-blue-600">
                    + {addons.filter(a => a.status === 'active').length} add-on{addons.filter(a => a.status === 'active').length !== 1 ? 's' : ''}
                  </span>
                )}
              </p>
              {addons && addons.filter(a => a.status === 'active').length > 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  Total: ${(
                    (getCurrentPlan()?.price || 0) +
                    addons.filter(a => a.status === 'active').reduce((sum, a) => sum + (a.price_cents / 100), 0)
                  ).toFixed(2)}/year
                </p>
              )}
            </div>
            <div className="text-right">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPlanStatusColor(subscription.status)}`}>
                {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
              </span>
              {subscription.current_period_end && (
                <p className="text-sm text-gray-500 mt-1">
                  Renews: {new Date(subscription.current_period_end).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          {subscription.cancel_at_period_end && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-yellow-800 text-sm">
                ⚠️ Your subscription will be canceled at the end of the current period.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Add-Ons Section */}
      {subscription && subscription.status === 'active' && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Add-Ons</h2>
            <button
              onClick={() => setShowAddonModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              Purchase Add-On
            </button>
          </div>
          <AddonManagement onAddonCancelled={() => refreshSubscription()} />
        </div>
      )}

      {/* Available Plans */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const isCurrentPlan = subscription?.plan_type === plan.type;
            const isUpgrade = subscription && !isCurrentPlan;

            return (
              <div
                key={plan.type}
                className={`relative bg-white rounded-lg shadow-sm border-2 p-6 ${
                  plan.popular
                    ? 'border-blue-500 ring-2 ring-blue-100'
                    : 'border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Current Plan
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">{plan.name}</h3>
                  <div className="text-3xl font-bold text-blue-600 mt-2">
                    ${plan.price}
                    <span className="text-lg font-normal text-gray-500">/year</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleUpgrade(plan.type)}
                  disabled={isCurrentPlan}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                    isCurrentPlan
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : isUpgrade
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {isCurrentPlan ? 'Current Plan' : isUpgrade ? 'Upgrade' : 'Subscribe Now'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Additional Features */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Included</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-green-500 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="font-medium text-gray-900">Priority Support</h4>
              <p className="text-sm text-gray-600">Get help when you need it most</p>
            </div>
          </div>
          <div className="flex items-start">
            <svg className="w-6 h-6 text-green-500 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="font-medium text-gray-900">Regular Updates</h4>
              <p className="text-sm text-gray-600">New features and improvements monthly</p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Upgrade Modal */}
      {showUpgradeModal && (
        <SubscriptionUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          requiredFeature={selectedPlan}
          featureName={`${plans.find(p => p.type === selectedPlan)?.name} Features`}
        />
      )}

      {/* Add-On Purchase Modal */}
      {showAddonModal && (
        <AddonPurchaseModal
          isOpen={showAddonModal}
          onClose={() => setShowAddonModal(false)}
          onSuccess={() => {
            setShowAddonModal(false);
            refreshSubscription();
          }}
        />
      )}
    </div>
  );
};

export default SubscriptionManagement;
